import { useCreateProperty, useLots, useProperties } from '@/api/hooks';
import { getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { ChevronRight, Eye, MapPin, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ACTIVITIES = [
  { value: 'breeding', label: 'Cria / reprodução' },
  { value: 'fattening', label: 'Engorda' },
  { value: 'dairy', label: 'Leite' },
  { value: 'rearing', label: 'Recria' },
  { value: 'feedlot', label: 'Confinamento' },
];

export function PropertiesPage() {
  const { data, isLoading } = useProperties();
  const createProperty = useCreateProperty();
  const { propertyId, setPropertyId } = usePropertyScope();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    city: '',
    state_code: '',
    activities: ['breeding'] as string[],
  });

  const properties = data?.properties ?? [];

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const result = await createProperty.mutateAsync(form);
      setPropertyId(result.property.id);
      setOpen(false);
      setForm({ name: '', city: '', state_code: '', activities: ['breeding'] });
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao criar fazenda.'));
    }
  }

  return (
    <div>
      <PageHeader
        title="Fazendas"
        subtitle="Cada fazenda contém seus lotes (pastos, currais…)"
        actions={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" />
            Nova fazenda
          </Button>
        }
      />

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-32 rounded-2xl bg-manejai-sand/40 animate-pulse" />
          ))}
        </div>
      ) : properties.length === 0 ? (
        <Card>
          <EmptyState
            icon={MapPin}
            title="Nenhuma fazenda"
            description="Cadastre sua primeira fazenda para começar o manejo."
            action={<Button onClick={() => setOpen(true)}>Cadastrar fazenda</Button>}
          />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {properties.map((p) => (
            <PropertyCard
              key={p.id}
              property={p}
              active={propertyId === p.id}
            />
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Nova fazenda"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="create-property-form-modal" loading={createProperty.isPending}>
              Salvar
            </Button>
          </>
        }
      >
        <form id="create-property-form-modal" onSubmit={handleCreate} className="space-y-4">
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Input label="Nome" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Cidade" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            <Input label="UF" maxLength={2} value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value.toUpperCase() })} />
          </div>
          <Select
            label="Atividade principal"
            value={form.activities[0]}
            onChange={(e) => setForm({ ...form, activities: [e.target.value] })}
            options={ACTIVITIES}
          />
        </form>
      </Modal>
    </div>
  );
}

function PropertyCard({
  property,
  active,
}: {
  property: { id: number; name: string; city: string | null; state_code: string | null; activities: string[] };
  active: boolean;
}) {
  const { data: lots } = useLots(property.id);
  const navigate = useNavigate();

  return (
    <Card
      className={`group cursor-pointer hover:shadow-elevated transition-shadow ${active ? 'ring-2 ring-manejai-sage' : ''}`}
      onClick={() => navigate(`/propriedades/${property.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/propriedades/${property.id}`);
        }
      }}
      tabIndex={0}
      role="link"
      aria-label={`Ver fazenda ${property.name}`}
    >
      <CardBody>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-manejai-forest">{property.name}</h3>
            <p className="text-sm text-manejai-bark/60 mt-0.5">
              {[property.city, property.state_code].filter(Boolean).join(' — ') || 'Local não informado'}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {active && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-manejai-moss bg-manejai-sage/20 px-2 py-1 rounded-full">
                Ativa
              </span>
            )}
            <Eye className="h-4 w-4 text-manejai-moss/50 group-hover:text-manejai-moss transition-colors" aria-hidden />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <p className="text-xs text-manejai-bark/50">
            {lots?.lots.length ?? 0} lote(s)
          </p>
          <span className="inline-flex items-center gap-0.5 text-xs font-medium text-manejai-moss group-hover:underline">
            Ver detalhes
            <ChevronRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </CardBody>
    </Card>
  );
}
